import { cn } from "~/lib/utils";

export type GradientStop = {
  color: string;
  position: number;
};

export type GradientType = "radial" | "linear";
export type RadialPosition =
  | "center"
  | "top"
  | "bottom"
  | "left"
  | "right"
  | "top-left"
  | "top-right"
  | "bottom-left"
  | "bottom-right";
export type LinearDirection =
  | "to-t"
  | "to-b"
  | "to-l"
  | "to-r"
  | "to-tl"
  | "to-tr"
  | "to-bl"
  | "to-br";

interface GradientBackgroundProps {
  type?: GradientType;
  stops?: GradientStop[];
  position?: RadialPosition | LinearDirection;
  className?: string;
  blur?: boolean;
  softness?: number;
}

const positionMap = {
  radial: {
    center: "center",
    top: "top center",
    bottom: "bottom center",
    left: "center left",
    right: "center right",
    "top-left": "top left",
    "top-right": "top right",
    "bottom-left": "bottom left",
    "bottom-right": "bottom right",
  },
  linear: {
    "to-t": "0deg",
    "to-tr": "45deg",
    "to-r": "90deg",
    "to-br": "135deg",
    "to-b": "180deg",
    "to-bl": "225deg",
    "to-l": "270deg",
    "to-tl": "315deg",
  },
};

/**
 * Gradient Presets
 *
 * Usage:
 * <GradientBackground {...presets.goldensun} />
 *
 * Blur Options:
 * - Default soft blur: <GradientBackground {...presets.goldensun} />
 * - Extra soft: <GradientBackground {...presets.goldensun} softness={150} />
 * - Less soft: <GradientBackground {...presets.goldensun} softness={50} />
 * - No blur: <GradientBackground {...presets.goldensun} blur={false} softness={0} />
 *
 * Available Presets:
 * - sunrise: Radial gradient from bottom, warm colors to navy
 * - goldensun: Same as sunrise but from top-right corner
 * - aurora: Linear gradient from top-left to bottom-right, green to blue to purple
 * - crisp: Vibrant, modern colors
 */
export const presets = {
  modern: {
    type: "radial" as GradientType,
    position: "top-right" as RadialPosition,
    stops: [
      { color: "#818CF8", position: 0 }, // Soft indigo
      { color: "#38BDF8", position: 40 }, // Bright sky
      { color: "#FFFFFF", position: 100 }, // Pure white
    ],
  },
  fresh: {
    type: "linear" as GradientType,
    position: "to-br" as LinearDirection,
    stops: [
      { color: "#22D3EE", position: 0 }, // Cyan
      { color: "#818CF8", position: 50 }, // Indigo
      { color: "#F0FDFA", position: 100 }, // Teal/white
    ],
  },
  minimal: {
    type: "radial" as GradientType,
    position: "top" as RadialPosition,
    stops: [
      { color: "#E2E8F0", position: 0 }, // Slate 200
      { color: "#F8FAFC", position: 100 }, // Slate 50
    ],
  },
  crisp: {
    type: "radial" as GradientType,
    position: "top-right" as RadialPosition,
    stops: [
      { color: "#0EA5E9", position: 0 }, // Bright sky blue
      { color: "#6366F1", position: 30 }, // Vibrant indigo
      { color: "#2DD4BF", position: 60 }, // Fresh teal
      { color: "#0F172A", position: 90 }, // Deep slate
    ],
  },
  sunrise: {
    type: "radial" as GradientType,
    position: "bottom" as RadialPosition,
    stops: [
      { color: "#FDC062", position: 0 },
      { color: "#BF5551", position: 25 },
      { color: "#163B5B", position: 50 },
      { color: "#021A38", position: 75 },
    ],
  },
  goldensun: {
    type: "radial" as GradientType,
    position: "top-right" as RadialPosition,
    stops: [
      { color: "#FDC062", position: 0 },
      { color: "#BF5551", position: 25 },
      { color: "#163B5B", position: 50 },
      { color: "#021A38", position: 75 },
    ],
  },
  aurora: {
    type: "linear" as GradientType,
    position: "to-br" as LinearDirection,
    stops: [
      { color: "#10B981", position: 0 },
      { color: "#3B82F6", position: 50 },
      { color: "#6366F1", position: 100 },
    ],
  },
};

export function GradientBackground({
  type = "radial",
  stops,
  position = type === "radial" ? "center" : "to-b",
  className,
  blur = true,
  softness = 75,
}: GradientBackgroundProps) {
  let activeStops = stops;
  if (!activeStops) {
    Object.entries(presets).forEach(([, preset]) => {
      if (preset.type === type && preset.position === position) {
        activeStops = preset.stops;
      }
    });
  }

  if (!activeStops) {
    activeStops = presets.sunrise.stops;
  }

  const gradientString =
    type === "radial"
      ? `radial-gradient(150% 150% at ${positionMap.radial[position as RadialPosition]}, ${activeStops.map((stop) => `${stop.color} ${stop.position}%`).join(", ")})`
      : `linear-gradient(${positionMap.linear[position as LinearDirection]}, ${activeStops.map((stop) => `${stop.color} ${stop.position}%`).join(", ")})`;

  const backgroundStyle = {
    background: gradientString,
    filter: blur && softness > 0 ? `blur(${softness}px)` : undefined,
    minHeight: "100vh",
  };

  return (
    <div
      className={cn("absolute inset-0 z-0 h-full w-full", className)}
      style={{
        ...backgroundStyle,
        transform: "translateZ(0)",
      }}
    />
  );
}

